<template>
  <div class="create-event-v2">

    <!-- Edit mode: loading / error -->
    <div v-if="isEditMode && loadingData" class="edit-loading">
      <i class="fas fa-spinner fa-spin fs-3 text-muted"></i>
      <p class="mt-2 text-muted">Carregando evento...</p>
    </div>
    <div v-else-if="isEditMode && loadError" class="edit-error">
      <i class="fas fa-exclamation-circle fs-2 text-danger mb-2"></i>
      <p>{{ loadError }}</p>
      <button class="btn btn-outline-danger btn-sm" @click="fetchEventForEdit">Tentar novamente</button>
    </div>

    <template v-else>

    <!-- ════════════════════════════════════════════════════════
         DESKTOP LAYOUT — split screen 30 / 60  (≥ md)
    ════════════════════════════════════════════════════════ -->
    <div class="desktop-layout d-none d-md-flex flex-column">

      <!-- Abas principais (apenas em modo edição) -->
      <div v-if="isEditMode" class="d-none d-md-flex align-items-end" style="background:#fff; border-bottom:2px solid #e9ecef; padding: 0.75rem 1.5rem 0; gap:0.25rem; flex-shrink:0;">
        <button
          @click="activeMainTab = 'evento'"
          :style="{
            padding: '0.6rem 1.25rem',
            background: activeMainTab === 'evento' ? '#fff' : '#f8f9fa',
            border: '1px solid #e9ecef',
            borderBottom: activeMainTab === 'evento' ? '2px solid #fff' : '1px solid #e9ecef',
            borderRadius: '8px 8px 0 0',
            marginBottom: '-2px',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: activeMainTab === 'evento' ? '#ff50a3' : '#6c757d',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            outline: 'none',
            transition: 'all 0.15s',
          }"
        >
          <i class="fas fa-calendar-alt"></i> Evento
        </button>
        <button
          @click="activeMainTab = 'convite'"
          :style="{
            padding: '0.6rem 1.25rem',
            background: activeMainTab === 'convite' ? '#fff' : '#f8f9fa',
            border: '1px solid #e9ecef',
            borderBottom: activeMainTab === 'convite' ? '2px solid #fff' : '1px solid #e9ecef',
            borderRadius: '8px 8px 0 0',
            marginBottom: '-2px',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: activeMainTab === 'convite' ? '#ff50a3' : '#6c757d',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            outline: 'none',
            transition: 'all 0.15s',
          }"
        >
          <i class="fas fa-envelope-open-text"></i> Convite
        </button>
        <button
          v-if="currentEventTypeHasRsvp"
          @click="activeMainTab = 'convidados'"
          :style="{
            padding: '0.6rem 1.25rem',
            background: activeMainTab === 'convidados' ? '#fff' : '#f8f9fa',
            border: '1px solid #e9ecef',
            borderBottom: activeMainTab === 'convidados' ? '2px solid #fff' : '1px solid #e9ecef',
            borderRadius: '8px 8px 0 0',
            marginBottom: '-2px',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: activeMainTab === 'convidados' ? '#ff50a3' : '#6c757d',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            outline: 'none',
            transition: 'all 0.15s',
          }"
        >
          <i class="fas fa-users"></i> Convidados
        </button>
      </div>

      <!-- Painel de convidados (desktop, edit mode) -->
      <div v-if="isEditMode && activeMainTab === 'convidados'" style="flex:1; overflow-y:auto; background:#f4f6f8; padding:1.5rem;">
        <GuestListManager
          :eventId="$route.params.id"
          :eventName="eventName"
          :eventShareUrl="eventShareUrl"
        />
      </div>

      <!-- Painel de convite (desktop, edit mode) -->
      <div v-if="isEditMode && activeMainTab === 'convite'" style="flex:1; display:flex; flex-direction:column; overflow:hidden;">
        <div style="flex:1; overflow-y:auto; background:#f4f6f8; display:flex; flex-direction:column; align-items:center; padding:1.5rem 1.5rem 0;">
          <div style="width:100%; max-width:700px;">
            <InviteForm
              :eventName="eventName"
              :initialData="inviteData"
              @update:inviteData="handleInviteDataUpdate"
            />
          </div>
        </div>
        <div class="split-right__footer">
            <button
              type="button"
              class="btn-create-event"
              style="margin-left: auto"
              @click="handleDesktopFinalize"
              :disabled="showLoader"
            >
              <i class="fas fa-check me-2"></i> Salvar alterações
            </button>
          </div>
      </div>

      <!-- Workspace -->
      <div v-show="!isEditMode || activeMainTab === 'evento'" class="split-workspace">

        <!-- ── LEFT PANEL 30% : Catálogo ────────────────── -->
        <aside class="split-left">

          <!-- FIXO: cabeçalho + select + adicionar personalizado -->
          <div class="split-left__fixed">
            <div class="panel-section-header">
              <h2 class="panel-section-header__title">
                <i class="fas fa-list-ul me-2"></i>Catálogo
              </h2>
              <p class="panel-section-header__sub">Escolha a ocasião e selecione presentes</p>
            </div>

            <!-- Dropdown de ocasião -->
            <div class="mb-3">
              <label class="form-label fw-semibold small">Qual é a ocasião?</label>
              <!-- Edit: tipo bloqueado -->
              <div v-if="isEditMode" class="locked-type-chip">
                <i class="fas fa-check-circle locked-type-chip__icon"></i>
                <span class="locked-type-chip__name">{{ editEventTypeName }}</span>
                <i class="fas fa-lock locked-type-chip__lock" title="O tipo não pode ser alterado"></i>
              </div>
              <!-- Create: select normal -->
              <div v-else class="occasion-select-wrap">
                <select
                  v-model="selectedEventType"
                  class="form-select occasion-select"
                  :disabled="loadingTypes"
                  @change="onEventTypeChange"
                >
                  <option value="">
                    {{ loadingTypes ? 'Carregando...' : 'Selecione a ocasião' }}
                  </option>
                  <option v-for="type in eventTypes" :key="type.id" :value="type.id">
                    {{ type.name }}
                  </option>
                </select>
              </div>
              <div v-if="errors.eventType" class="text-danger small mt-1">
                <i class="fas fa-exclamation-circle me-1"></i>{{ errors.eventType }}
              </div>
            </div>

            <!-- Adicionar presente personalizado (fixo entre select e lista) -->
            <div v-if="selectedEventType" class="split-left__add-custom">
              <button type="button" class="btn-add-custom-link" @click="openAddCustomModal">
                <i class="fas fa-plus-circle me-1"></i> Adicionar presente personalizado
              </button>
            </div>
          </div>

          <!-- ROLÁVEL: lista do catálogo -->
          <div class="split-left__scroll">
            <!-- Loading items -->
            <div v-if="loadingItems" class="catalog-loading">
              <i class="fas fa-spinner fa-spin me-2"></i> Buscando presentes...
            </div>

            <!-- Empty: no type selected -->
            <div v-else-if="!selectedEventType" class="catalog-empty">
              <i class="fas fa-gift catalog-empty__icon"></i>
              <p>Selecione uma ocasião para ver presentes sugeridos</p>
            </div>

            <!-- Empty: type selected but no items -->
            <div v-else-if="selectedEventType && eventItems.length === 0" class="catalog-empty">
              <p>Nenhum presente sugerido para esta ocasião.</p>
            </div>

            <!-- Catalog items list -->
            <div v-else class="catalog-list">
              <div
                v-for="item in eventItems"
                :key="item.id"
                class="catalog-row"
                :class="{ 'catalog-row--selected': item.isSelected }"
              >
                <img
                  :src="item.imageUrl || giftPlaceholder"
                  :alt="item.name"
                  class="catalog-row__img"
                />
                <div class="catalog-row__info">
                  <span class="catalog-row__name">{{ item.name }}</span>
                  <span class="catalog-row__price">R$ {{ formatCurrency(item.price) }}</span>
                </div>
                <div class="catalog-row__actions">
                  <span v-if="item.isLocked" class="catalog-row__locked-badge" title="Item já no evento">
                    <i class="fas fa-lock"></i>
                  </span>
                  <button
                    v-else-if="!item.isSelected"
                    type="button"
                    class="catalog-row__btn catalog-row__btn--add"
                    @click.stop="toggleItem(item.id)"
                  >
                    <i class="fas fa-plus"></i>
                    <span>Adicionar</span>
                  </button>
                  <button
                    v-else
                    type="button"
                    class="catalog-row__btn catalog-row__btn--added"
                    @click.stop="toggleItem(item.id)"
                    title="Clique para remover"
                  >
                    <i class="fas fa-check"></i>
                    <span>Adicionado</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

        </aside>

        <!-- ── RIGHT PANEL 60% : Meu evento ─────────────── -->
        <main class="split-right">

          <!-- FIXO: card de identidade do evento -->
          <div class="split-right__fixed">
            <div class="event-identity-card">
              <div class="event-identity-card__photo-col">
                <PhotoUploadButton
                  :preview="eventPhotoPreview"
                  empty-label="Foto"
                  label="Adicionar foto do evento"
                  :has-error="!!errors.eventPhoto"
                  @click="triggerDesktopFileInput"
                  @remove="removePhoto"
                />
                <input
                  type="file"
                  ref="desktopFileInput"
                  accept="image/*"
                  class="d-none"
                  @change="handlePhotoUpload"
                />
              </div>
              <div class="event-identity-card__name-col">
                <label class="event-identity-card__label">Nome do evento</label>
                <input
                  type="text"
                  class="event-identity-card__input"
                  :class="{ 'is-invalid': errors.eventName }"
                  v-model="eventName"
                  :placeholder="eventNamePlaceholder"
                  autocomplete="off"
                  @input="errors.eventName = null"
                />
                <p class="event-identity-card__hint">Ex: Casamento de Elton e Mariana</p>
                <div v-if="errors.eventName" class="invalid-feedback d-block">{{ errors.eventName }}</div>
              </div>
            </div>
          </div>

          <!-- ROLÁVEL: lista de presentes selecionados -->
          <div class="split-right__scroll">
            <div class="selected-section">
              <div class="selected-section__header">
                <h3 class="selected-section__title">
                  <i class="fas fa-heart me-2"></i>
                  Meus presentes
                  <span v-if="selectedItemsList.length > 0" class="selected-section__badge">
                    {{ selectedItemsList.length }}
                  </span>
                </h3>
                <span v-if="totalSelectedValue > 0" class="selected-section__total">
                  Total: <strong>R$ {{ formatCurrency(totalSelectedValue) }}</strong>
                </span>
              </div>

              <!-- Empty state -->
              <div v-if="selectedItemsList.length === 0" class="selected-empty">
                <div class="selected-empty__icon"><i class="fas fa-gift"></i></div>
                <p class="selected-empty__text">Nenhum presente selecionado ainda</p>
                <p class="selected-empty__hint">Selecione a ocasião ao lado e adicione presentes à sua lista</p>
              </div>

              <!-- Items list -->
              <transition-group v-else name="item-list" tag="div" class="selected-list">
                <div
                  v-for="item in selectedItemsList"
                  :key="item.id"
                  class="selected-row"
                >
                  <img
                    :src="item.imageUrl || giftPlaceholder"
                    :alt="item.name"
                    class="selected-row__img"
                  />
                  <div class="selected-row__info">
                    <span class="selected-row__name">{{ item.name }}</span>
                    <span class="selected-row__price">R$ {{ formatCurrency(item.price) }}</span>
                    <span v-if="item.isCustom" class="selected-row__badge">Personalizado</span>
                  </div>
                  <div class="selected-row__actions">
                    <span v-if="item.isLocked" class="selected-row__locked" title="Item já no evento">
                      <i class="fas fa-lock"></i>
                    </span>
                    <template v-else>
                      <button
                        type="button"
                        class="selected-row__btn selected-row__btn--edit"
                        title="Editar"
                        @click="openEditModal(item)"
                      >
                        <i class="fas fa-pencil-alt"></i>
                      </button>
                      <button
                        type="button"
                        class="selected-row__btn selected-row__btn--remove"
                        title="Remover"
                        @click="removeSelectedItem(item.id)"
                      >
                        <i class="fas fa-times"></i>
                      </button>
                    </template>
                  </div>
                </div>
              </transition-group>

            </div>
          </div>

          <!-- Right panel footer: create button + add custom -->
          <div class="split-right__footer">
            <button
              v-if="selectedEventType"
              type="button"
              class="btn-add-custom-footer"
              @click="openAddCustomModal"
            >
              <i class="fas fa-plus-circle me-1"></i>
              Presente personalizado
            </button>
            <button
              type="button"
              class="btn-create-event"
              @click="handleDesktopFinalize"
              :disabled="showLoader"
            >
              <i class="fas fa-check me-2"></i>
              {{ isEditMode ? 'Salvar alterações' : 'Criar meu evento' }}
            </button>
          </div>
        </main>

      </div><!-- /split-workspace -->

      <!-- ── MODAIS (desktop) ───────────────────────────── -->

      <!-- Edit item (price / name for custom) -->
      <BottomModal
        :visible="editModal.visible"
        @close="closeEditModal"
        :fullHeight="false"
        :showCloseButton="true"
      >
        <template #default>
          <div class="item-modal-content p-4" v-if="editModal.item">
            <h5 class="text-center mb-1">Editar presente</h5>

            <!-- Photo (always shown, clickable to change) -->
            <div class="d-flex justify-content-center mb-3">
              <PhotoUploadButton
                :preview="editModal.editedImagePreview"
                empty-label="Foto"
                label="Trocar foto"
                @click="triggerEditItemFileInput"
                @remove="removeEditItemImage"
              />
            </div>
            <input
              ref="editItemFileInput"
              type="file"
              accept="image/jpeg,image/png"
              class="d-none"
              @change="handleEditItemImageUpload"
            />

            <!-- Name: editable only for custom items -->
            <div v-if="editModal.item.isCustom" class="form-group mb-3">
              <label class="form-label fw-semibold">Nome do presente</label>
              <input
                type="text"
                class="form-control"
                v-model="editModal.editedName"
                :class="{ 'is-invalid': editModal.errors.name }"
              />
              <div v-if="editModal.errors.name" class="invalid-feedback d-block">{{ editModal.errors.name }}</div>
            </div>

            <div class="form-group mb-4">
              <label class="form-label fw-semibold">Valor (R$)</label>
              <CurrencyInput
                :options="currencyInputOptions"
                class="form-control"
                v-model="editModal.editedPrice"
                placeholder="0,00"
                :class="{ 'is-invalid': editModal.errors.price }"
              />
              <div v-if="editModal.errors.price" class="invalid-feedback d-block">{{ editModal.errors.price }}</div>
            </div>

            <div class="modal-buttons-row">
              <button type="button" class="btn btn-outline-secondary flex-grow-1" @click="closeEditModal">
                <i class="fas fa-times me-2"></i> Cancelar
              </button>
              <button type="button" class="btn btn-modal-confirm flex-grow-1" @click="confirmEditModal">
                <i class="fas fa-check me-2"></i> Salvar
              </button>
            </div>
          </div>
        </template>
      </BottomModal>

      <!-- Add custom item modal -->
      <BottomModal
        :visible="customItemModal.visible"
        @close="closeAddCustomModal"
        :fullHeight="false"
        :showCloseButton="true"
      >
        <template #default>
          <div class="item-modal-content p-3">
            <h5 class="text-center mb-1 text-primary-emphasis">Novo presente personalizado</h5>
            <p class="text-center text-muted-dark mb-4">Adicione um presente que não está na lista sugerida.</p>

            <div class="d-flex justify-content-center mb-4">
              <PhotoUploadButton
                :preview="customItemModal.imagePreview"
                empty-label="Foto"
                label="Adicionar foto do presente"
                @click="triggerCustomItemFileInput"
                @remove="removeCustomItemImage"
              />
            </div>
            <input
              ref="customItemFileInput"
              type="file"
              accept="image/jpeg,image/png"
              class="d-none"
              @change="handleCustomItemImageUpload"
            />

            <div class="form-group mb-3">
              <label class="form-label fw-semibold">Nome do presente</label>
              <input
                type="text"
                class="form-control"
                v-model="customItemModal.name"
                :placeholder="customItemPlaceholder"
                :class="{ 'is-invalid': customItemModal.errors.name }"
              />
              <div v-if="customItemModal.errors.name" class="invalid-feedback d-block">{{ customItemModal.errors.name }}</div>
            </div>

            <div class="form-group mb-3">
              <label class="form-label fw-semibold">Valor (R$)</label>
              <CurrencyInput
                :options="currencyInputOptions"
                class="form-control"
                v-model="customItemModal.price"
                placeholder="0,00"
                :class="{ 'is-invalid': customItemModal.errors.price }"
              />
              <div v-if="customItemModal.errors.price" class="invalid-feedback d-block">{{ customItemModal.errors.price }}</div>
            </div>

            <div class="modal-buttons-row mt-4">
              <button type="button" class="btn btn-outline-secondary flex-grow-1" @click="closeAddCustomModal">
                <i class="fas fa-times me-2"></i> Cancelar
              </button>
              <button type="button" class="btn btn-modal-confirm flex-grow-1" @click="addCustomItem">
                <i class="fas fa-plus me-2"></i> Adicionar presente
              </button>
            </div>
          </div>
        </template>
      </BottomModal>

      <!-- Confirm change event type modal -->
      <BottomModal
        :visible="confirmTypeChangeModal.visible"
        @close="cancelTypeChange"
        :fullHeight="false"
        :showCloseButton="true"
      >
        <template #default>
          <div class="item-modal-content p-4 text-center">
            <div class="mb-3" style="font-size: 2.5rem;">⚠️</div>
            <h5 class="mb-2">Trocar ocasião?</h5>
            <p class="text-muted small mb-4">
              Você tem <strong>{{ selectedItemsList.length }} presente(s)</strong> adicionado(s).
              Ao trocar a ocasião, todos os itens da lista serão perdidos.
            </p>
            <div class="modal-buttons-row">
              <button type="button" class="btn btn-outline-secondary flex-grow-1" @click="cancelTypeChange">
                Cancelar
              </button>
              <button type="button" class="btn btn-danger flex-grow-1" @click="confirmTypeChange">
                <i class="fas fa-trash me-2"></i> Sim, trocar
              </button>
            </div>
          </div>
        </template>
      </BottomModal>

      <!-- Photo cropper -->
      <ImageCropperModal
        :visible="cropperModal.visible"
        :src="cropperModal.src"
        :aspect-ratio="1"
        :title="cropperModal.forCustomItem ? 'Ajuste a foto do presente' : 'Ajuste a foto do evento'"
        subtitle="Posicione e enquadre a imagem como preferir."
        @confirm="onCropConfirm"
        @cancel="cancelCrop"
      />

    </div>
    <!-- /desktop-layout -->


    <!-- ════════════════════════════════════════════════════════
         MOBILE LAYOUT — cart-style (< md)
    ════════════════════════════════════════════════════════ -->
    <div class="mobile-layout d-md-none">

      <!-- ── CABEÇALHO COLAPSÁVEL: foto + nome ──────── -->
      <div class="mob-header" :class="{ 'mob-header--collapsed': mobileHeaderCollapsed }">
        <div class="mob-header__inner">
          <!-- Foto do evento -->
          <div class="mob-header__photo">
            <PhotoUploadButton
              :preview="eventPhotoPreview"
              empty-label="Foto"
              label="Foto"
              :has-error="!!errors.eventPhoto"
              @click="triggerMobileFileInput"
              @remove="removePhoto"
            />
            <input type="file" ref="mobileFileInput" accept="image/*" class="d-none" @change="handlePhotoUpload" />
          </div>
          <!-- Nome do evento -->
          <div class="mob-header__name-wrap">
            <label class="mob-header__label">Nome do evento</label>
            <input
              type="text"
              class="mob-header__input"
              :class="{ 'is-invalid': errors.eventName }"
              v-model="eventName"
              :placeholder="eventNamePlaceholder"
              autocomplete="off"
              @input="errors.eventName = null"
            />
            <div v-if="errors.eventName" class="invalid-feedback d-block">{{ errors.eventName }}</div>
          </div>
        </div>
      </div>

      <!-- ── ABAS: Sugestões | Meus itens ───────────── -->
      <div class="mob-tabs" style="display:none">
        <button
          v-if="isEditMode"
          :class="['mob-tab', { 'mob-tab--active': mobileActiveTab === 'items' }]"
          @click="mobileActiveTab = 'items'"
        >
          Meus itens
          <span v-if="selectedItemsList.length" class="mob-tab__badge">{{ selectedItemsList.length }}</span>
        </button>
        <button
          :class="['mob-tab', { 'mob-tab--active': mobileActiveTab === 'catalog' }]"
          @click="mobileActiveTab = 'catalog'"
        >
          <i class="fas fa-list-ul me-1"></i> Sugestões
        </button>
        <button
          v-if="!isEditMode"
          :class="['mob-tab', { 'mob-tab--active': mobileActiveTab === 'items' }]"
          @click="mobileActiveTab = 'items'"
        >
          Meus itens
          <span v-if="selectedItemsList.length" class="mob-tab__badge">{{ selectedItemsList.length }}</span>
        </button>
        <button
          v-if="isEditMode"
          :class="['mob-tab', { 'mob-tab--active': mobileActiveTab === 'convite' }]"
          @click="mobileActiveTab = 'convite'"
        >
          <i class="fas fa-envelope-open-text me-1"></i> Convite
        </button>
        <button
          v-if="isEditMode && currentEventTypeHasRsvp"
          :class="['mob-tab', { 'mob-tab--active': mobileActiveTab === 'convidados' }]"
          @click="mobileActiveTab = 'convidados'"
        >
          <i class="fas fa-users me-1"></i> Convidados
        </button>
      </div>

      <!-- ── CONTEÚDO ROLÁVEL ────────────────────────── -->
      <div
        class="mob-content"
        @scroll="onMobileScroll"
        ref="mobContent"
      >

        <!-- ── ABA: CATÁLOGO ────────────────────────── -->
        <div v-show="mobileActiveTab === 'catalog'" class="mob-catalog">

          <!-- Select de ocasião -->
          <div class="mob-occasion-wrap">
            <label class="mob-occasion-label">Qual é a ocasião?</label>
            <!-- Edit: tipo bloqueado -->
            <div v-if="isEditMode" class="locked-type-chip">
              <i class="fas fa-check-circle locked-type-chip__icon"></i>
              <span class="locked-type-chip__name">{{ editEventTypeName }}</span>
              <i class="fas fa-lock locked-type-chip__lock"></i>
            </div>
            <!-- Create: select normal -->
            <div v-else class="occasion-select-wrap">
              <select
                v-model="selectedEventType"
                class="form-select occasion-select"
                :disabled="loadingTypes"
                @change="onEventTypeChange"
              >
                <option value="">{{ loadingTypes ? 'Carregando...' : 'Selecione a ocasião' }}</option>
                <option v-for="type in eventTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
              </select>
            </div>
            <div v-if="errors.eventType" class="text-danger small mt-1">
              <i class="fas fa-exclamation-circle me-1"></i>{{ errors.eventType }}
            </div>
          </div>

          <!-- Loading -->
          <div v-if="loadingItems" class="catalog-loading">
            <i class="fas fa-spinner fa-spin me-2"></i> Buscando presentes...
          </div>

          <!-- Empty: sem ocasião -->
          <div v-else-if="!selectedEventType" class="catalog-empty">
            <i class="fas fa-gift catalog-empty__icon"></i>
            <p>Selecione uma ocasião para ver presentes sugeridos</p>
          </div>

          <!-- Empty: sem itens -->
          <div v-else-if="selectedEventType && eventItems.length === 0" class="catalog-empty">
            <p>Nenhum presente sugerido para esta ocasião.</p>
          </div>

          <!-- Lista de itens (compacta) -->
          <div v-else class="mob-catalog-list">
            <div
              v-for="item in eventItems"
              :key="item.id"
              class="catalog-row"
              :class="{ 'catalog-row--selected': item.isSelected }"
            >
              <img :src="item.imageUrl || giftPlaceholder" :alt="item.name" class="catalog-row__img" />
              <div class="catalog-row__info">
                <span class="catalog-row__name">{{ item.name }}</span>
                <span class="catalog-row__price">R$ {{ formatCurrency(item.price) }}</span>
              </div>
              <div class="catalog-row__actions">
                <span v-if="item.isLocked" class="catalog-row__locked-badge" title="Item já no evento">
                  <i class="fas fa-lock"></i>
                </span>
                <button
                  v-else-if="!item.isSelected"
                  type="button"
                  class="catalog-row__btn catalog-row__btn--add"
                  @click.stop="toggleItem(item.id)"
                >
                  <i class="fas fa-plus"></i>
                  <span>Adicionar</span>
                </button>
                <button
                  v-else
                  type="button"
                  class="catalog-row__btn catalog-row__btn--added"
                  @click.stop="toggleItem(item.id)"
                  title="Clique para remover"
                >
                  <i class="fas fa-check"></i>
                  <span>Adicionado</span>
                </button>
              </div>
            </div>
          </div>

        </div><!-- /mob-catalog -->

        <!-- ── ABA: MEUS ITENS ──────────────────────── -->
        <div v-show="mobileActiveTab === 'items'" class="mob-items-tab">

          <!-- Empty state -->
          <div v-if="selectedItemsList.length === 0" class="selected-empty">
            <div class="selected-empty__icon"><i class="fas fa-gift"></i></div>
            <p class="selected-empty__text">Nenhum presente selecionado ainda</p>
            <p class="selected-empty__hint">Volte às Sugestões e adicione presentes à sua lista</p>
          </div>

          <!-- Lista de presentes selecionados -->
          <transition-group v-else name="item-list" tag="div" class="selected-list">
            <div
              v-for="item in selectedItemsList"
              :key="item.id"
              class="selected-row"
            >
              <img :src="item.imageUrl || giftPlaceholder" :alt="item.name" class="selected-row__img" />
              <div class="selected-row__info">
                <span class="selected-row__name">{{ item.name }}</span>
                <span class="selected-row__price">R$ {{ formatCurrency(item.price) }}</span>
                <span v-if="item.isCustom" class="selected-row__badge">Personalizado</span>
              </div>
              <div class="selected-row__actions">
                  <span v-if="item.isLocked" class="selected-row__locked" title="Item já no evento">
                    <i class="fas fa-lock"></i>
                  </span>
                  <template v-else>
                    <button
                      type="button"
                      class="selected-row__btn selected-row__btn--edit"
                      title="Editar"
                      @click="openEditModal(item)"
                    ><i class="fas fa-pencil-alt"></i></button>
                    <button
                      type="button"
                      class="selected-row__btn selected-row__btn--remove"
                      title="Remover"
                      @click="removeSelectedItem(item.id)"
                    ><i class="fas fa-times"></i></button>
                  </template>
                </div>
            </div>
          </transition-group>

        </div><!-- /mob-items -->

        <!-- ── ABA: CONVITE (edit mode only) ──────── -->
        <div v-if="isEditMode && currentEventTypeHasRsvp" v-show="mobileActiveTab === 'convidados'" class="mob-convite-tab">
          <GuestListManager
            :eventId="$route.params.id"
            :eventName="eventName"
            :eventShareUrl="eventShareUrl"
          />
        </div>

        <div v-if="isEditMode" v-show="mobileActiveTab === 'convite'" class="mob-convite-tab">
          <InviteForm
            :eventName="eventName"
            :initialData="inviteData"
            @update:inviteData="handleInviteDataUpdate"
          />
          <p class="text-muted text-center mt-2 mb-4 small">
            <i class="fas fa-info-circle me-1"></i>
            Os dados do convite serão salvos ao clicar em "Salvar alterações".
          </p>
        </div>

      </div><!-- /mob-content -->

      <!-- ── BOTTOM NAV BAR ────────────────────────── -->
      <nav :class="['mob-bottom-nav', { 'mob-bottom-nav--visible': isEditMode }]">
        <button
          :class="['mob-bnav-item', { 'mob-bnav-item--active': mobileActiveTab === 'catalog' }]"
          @click="mobileActiveTab = 'catalog'"
        >
          <i class="fas fa-gift"></i>
          <span>Sugestões</span>
        </button>
        <button
          :class="['mob-bnav-item', { 'mob-bnav-item--active': mobileActiveTab === 'items' }]"
          @click="mobileActiveTab = 'items'"
        >
          <i class="fas fa-heart"></i>
          <span>Meus itens</span>
          <span v-if="selectedItemsList.length" class="mob-bnav-badge">{{ selectedItemsList.length }}</span>
        </button>
        <button
          v-if="isEditMode"
          :class="['mob-bnav-item', { 'mob-bnav-item--active': mobileActiveTab === 'convite' }]"
          @click="mobileActiveTab = 'convite'"
        >
          <i class="fas fa-envelope-open-text"></i>
          <span>Convite</span>
        </button>
        <button
          v-if="isEditMode && currentEventTypeHasRsvp"
          :class="['mob-bnav-item', { 'mob-bnav-item--active': mobileActiveTab === 'convidados' }]"
          @click="mobileActiveTab = 'convidados'"
        >
          <i class="fas fa-users"></i>
          <span>Convidados</span>
        </button>
      </nav>

      <!-- ── FAB: + Presente personalizado ──────────── -->
      <button
        v-if="selectedEventType && (mobileActiveTab === 'catalog' || mobileActiveTab === 'items')"
        class="mob-fab"
        @click="openAddCustomModal"
        title="Adicionar presente personalizado"
      >
        <i class="fas fa-plus"></i>
      </button>

      <!-- ── SAVE BAR: convite tab mobile ──────────── -->
      <div class="mob-convite-save-bar" v-if="isEditMode" v-show="mobileActiveTab === 'convite'">
        <button
          class="mob-cart-bar__btn w-100"
          @click="handleDesktopFinalize"
          :disabled="showLoader"
        >
          <i class="fas fa-check me-1"></i> Salvar alterações
        </button>
      </div>

      <!-- ── STICKY FOOTER: carrinho + criar ────────── -->
      <div class="mob-cart-bar" v-show="mobileActiveTab !== 'convite' && mobileActiveTab !== 'convidados'" :style="isEditMode ? 'bottom: 62px' : 'bottom: 0'">
        <div class="mob-cart-bar__summary" @click="mobileActiveTab = 'items'">
          <span class="mob-cart-bar__count">
            <i class="fas fa-heart me-1"></i>
            {{ selectedItemsList.length }}
            {{ selectedItemsList.length === 1 ? 'item' : 'itens' }}
          </span>
          <span class="mob-cart-bar__total" v-if="totalSelectedValue > 0">
            R$ {{ formatCurrency(totalSelectedValue) }}
          </span>
        </div>
        <button
          class="mob-cart-bar__btn"
          @click="handleDesktopFinalize"
          :disabled="showLoader"
        >
          <i class="fas fa-check me-1"></i>
          {{ isEditMode ? 'Salvar alterações' : 'Criar evento' }}
        </button>
      </div>

    </div>
    <!-- /mobile-layout -->


    <!-- ════════════════════════════════════════════
         Shared: loader + success modal
    ═══════════════════════════════════════════════ -->
    <EventSuccessModal
      :show="showEventSuccessModal"
      :eventId="createdEventId"
      :eventName="createdEventName"
      :eventShareUrl="createdEventShareUrl"
      :hasRsvp="createdEventHasRsvp"
      :account-blocked="accountBlocked"
      @close="showEventSuccessModal = false"
      @go-to-event="(id) => $router.replace(`/my-account/events/${id}/edit`)"
    />
    <FullScreenLoader v-if="showLoader" :message="loaderMessage" />

    </template><!-- /v-else (not loading/error in edit mode) -->

  </div>
</template>

<script>
import { toast } from 'vue3-toastify';
import { useAuthStore } from '@/stores/authStore';
import { v4 as uuidv4 } from 'uuid';
import api from '@/services/api';
import { getFees } from '@/services/feesService';
import { formatCurrency } from '@/utils/currencyFormatter';
import { setWithExpiry, getWithExpiry } from '@/utils/localStorageCache';
import giftCustomImage from '@/assets/images/gift-custom.png';

// Components
import BottomModal from '@/components/BottomModal.vue';
import CurrencyInput from '@/components/CurrencyInput.vue';
import ImageCropperModal from '@/components/ImageCropperModal.vue';
import PhotoUploadButton from '@/components/PhotoUploadButton.vue';
import FullScreenLoader from '@/components/FullScreenLoader.vue';
import EventSuccessModal from '@/components/EventSuccessModal.vue';
import InviteForm from '@/components/CreateEventSteps/InviteForm.vue';
import GuestListManager from '@/components/GuestListManager.vue';

// Mobile step components (unchanged)
import Step2Details from '@/components/CreateEventSteps/Step2EventDetails.vue';
import Step3Confirmation from '@/components/CreateEventSteps/Step3Confirmation.vue';

const CACHE_KEY_EVENT_TYPES = 'cachedEventTypes';
const CACHE_TTL_HOURS = 8;

export default {
  name: 'EventCreate',
  setup() {
    const authStore = useAuthStore();
    return { authStore };
  },
  components: {
    BottomModal,
    CurrencyInput,
    ImageCropperModal,
    PhotoUploadButton,
    FullScreenLoader,
    EventSuccessModal,
    InviteForm,
    GuestListManager,
    Step2Details,
    Step3Confirmation,
  },

  data() {
    return {
      // ── Desktop: catalog state ─────────────────────
      eventTypes: [],
      selectedEventType: '',
      _previousEventType: '',
      eventItems: [],
      loadingTypes: false,
      loadingItems: false,

      // ── Desktop: event identity ────────────────────
      eventName: '',
      eventPhoto: null,       // File
      eventPhotoPreview: null,

      // ── Desktop: confirm type change modal ─────────
      confirmTypeChangeModal: {
        visible: false,
        pendingTypeId: null,
      },

      // ── Desktop: edit modal ───────────────────────
      editModal: {
        visible: false,
        item: null,
        editedName: '',
        editedPrice: null,
        editedImagePreview: null,
        errors: {},
      },

      // ── Desktop: add custom modal ─────────────────
      customItemModal: {
        visible: false,
        name: '',
        price: null,
        imagePreview: null,
        imageBlob: null,
        errors: {},
      },

      // ── Desktop: photo cropper ────────────────────
      cropperModal: {
        visible: false,
        src: '',
        forCustomItem: false,
        forEditItem: false,
      },

      // ── Desktop: validation ───────────────────────
      errors: {},

      // Placeholder rotation
      customItemPlaceholder: 'Ex: Bolo de 3 andares',
      eventNamePlaceholder: 'Ex: Festa de Aniversário da Maria',
      _eventNamePlaceholders: [
        'Ex: Festa de Aniversário da Maria',
        'Ex: Casamento de Ana e João',
        'Ex: Chá de Bebê da Bia',
        'Ex: Formatura do Pedro',
        'Ex: Casa nova de Lucas e Julia',
      ],
      _placeholderIndex: 0,
      _placeholderTimer: null,

      // ── Shared: API / success ──────────────────────
      eventId: null,
      showLoader: false,
      loaderMessage: 'Criando seu evento...',
      showEventSuccessModal: false,
      createdEventId: null,
      createdEventName: null,
      createdEventShareUrl: null,
      createdEventHasRsvp: false,
      valueLimits: {
        min_event_item_price: 10,
        max_event_item_price: 5000,
        max_cart_amount: 20000,
      },

      // ── Edit mode ─────────────────────────────────
      existingItems: [],
      loadingData: false,
      loadError: '',
      photoRemoved: false,
      editEventTypeName: '',

      // ── Mobile: cart-style state ───────────────────
      mobileActiveTab: 'catalog',   // 'catalog' | 'items' | 'convite' | 'convidados'
      mobileHeaderCollapsed: false,

      // ── Aba principal (edit mode: evento / convite) ──
      activeMainTab: this.$route.query.tab || 'evento',
      inviteData: { location: '', dressCode: '', photos: [] },
      removedInvitePhotoURLs: [],

      // ── Mobile: step state (kept for compat) ───────
      currentStep: 1,
      steps: [
        { id: 1, name: 'Detalhes' },
        { id: 2, name: 'Confirmação' },
      ],
      mobileEventData: {
        eventId: null,
        eventType: null,
        eventTypeDisplay: '',
        selectedItems: {},
        eventName: '',
        eventDate: '',
        eventPhotoUrl: null,
        eventPhotoFile: null,
      },
    };
  },

  computed: {
    isEditMode() {
      return !!this.$route?.params?.id;
    },
    accountBlocked() {
      return this.authStore.isActivationPending;
    },
    selectedItemsList() {
      return this.eventItems
        .filter(i => i.isSelected)
        .sort((a, b) => (b.selectedAt || 0) - (a.selectedAt || 0));
    },
    totalSelectedValue() {
      return this.selectedItemsList.reduce((sum, i) => sum + (parseFloat(i.price) || 0), 0);
    },
    currencyInputOptions() {
      return {
        locale: 'pt-BR',
        currency: 'BRL',
        currencyDisplay: 'symbol',
        precision: 2,
        hideCurrencySymbolOnFocus: false,
        hideGroupingSeparatorOnFocus: false,
        autoDecimalDigits: true,
      };
    },
    giftPlaceholder() {
      return giftCustomImage;
    },
    currentEventTypeHasRsvp() {
      if (!this.selectedEventType || !this.eventTypes.length) return false;
      const type = this.eventTypes.find(t => t.id === this.selectedEventType);
      return type ? !!type.has_rsvp : false;
    },
    eventShareUrl() {
      const id = this.eventId || this.$route?.params?.id;
      return id ? `${window.location.origin}/event/${id}` : '';
    },
  },

  created() {
    if (!this.$route?.params?.id) {
      this.eventId = uuidv4();
      this.mobileEventData.eventId = this.eventId;
    }
  },

  beforeRouteUpdate(to, from, next) {
    // Rota mudou (ex: de edit → create): resetar estado
    if (!to.params.id) {
      this._resetToCreateMode();
    }
    next();
  },

  watch: {
    '$route'(to) {
      if (!to.params.id) {
        this._resetToCreateMode();
      }
    },
    activeMainTab(val) {
      const current = this.$route.query.tab;
      if (current !== val) {
        this.$router.replace({ query: { ...this.$route.query, tab: val } });
      }
      // Sync mobile
      if (val === 'convite') this.mobileActiveTab = 'convite';
      else if (val === 'convidados') this.mobileActiveTab = 'convidados';
      else this.mobileActiveTab = 'catalog';
    },
    mobileActiveTab(val) {
      const desktopTab = (val === 'convite' || val === 'convidados') ? val : 'evento';
      if (this.activeMainTab !== desktopTab) {
        this.activeMainTab = desktopTab;
      }
    },
  },

  mounted() {
    this.fetchValueLimits();
    if (this.isEditMode) {
      const tab = this.$route.query.tab;
      if (tab === 'convite') this.mobileActiveTab = 'convite';
      else if (tab === 'convidados') this.mobileActiveTab = 'convidados';
      else this.mobileActiveTab = 'items';
      this.fetchEventForEdit();
      this.fetchEventTypes();
    } else {
      this.fetchEventTypes();
    }
    this._startPlaceholderRotation();
  },

  beforeUnmount() {
    if (this._placeholderTimer) clearInterval(this._placeholderTimer);
    if (this.eventPhotoPreview && this.eventPhotoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(this.eventPhotoPreview);
    }
  },

  methods: {
    formatCurrency,

    async fetchValueLimits() {
      try {
        const config = await getFees();
        const limits = config?.limits || {};
        this.valueLimits = {
          min_event_item_price: Number(limits.min_event_item_price ?? limits.event_item?.min_price ?? this.valueLimits.min_event_item_price),
          max_event_item_price: Number(limits.max_event_item_price ?? limits.event_item?.max_price ?? this.valueLimits.max_event_item_price),
          max_cart_amount: Number(limits.max_cart_amount ?? limits.cart?.max_amount ?? this.valueLimits.max_cart_amount),
        };
      } catch (error) {
        console.warn('Não foi possível carregar limites de valores.', error);
      }
    },

    formatLimitCurrency(value) {
      return `R$ ${formatCurrency(Number(value) || 0)}`;
    },

    validateItemPriceLimit(price) {
      const value = Number(price);
      const min = Number(this.valueLimits.min_event_item_price) || 10;
      const max = Number(this.valueLimits.max_event_item_price) || 5000;
      if (!value || value <= 0) return 'O valor deve ser positivo.';
      if (value < min || value > max) {
        return `O valor deve ficar entre ${this.formatLimitCurrency(min)} e ${this.formatLimitCurrency(max)}.`;
      }
      return '';
    },

    validateSelectedItemsLimits(items = this.selectedItemsList) {
      for (const item of items) {
        const priceError = this.validateItemPriceLimit(item?.price);
        if (priceError) return priceError;
      }
      const total = items.reduce((sum, item) => sum + ((Number(item?.price) || 0) * (Number(item?.quantity) || 1)), 0);
      const maxCart = Number(this.valueLimits.max_cart_amount) || 20000;
      if (total > maxCart) return `O valor máximo por carrinho é de ${this.formatLimitCurrency(maxCart)}.`;
      return '';
    },

    // ── Reset para modo criação ────────────────────────
    _resetToCreateMode() {
      this.eventId = uuidv4();
      this.eventName = '';
      this.eventPhoto = null;
      this.eventPhotoPreview = null;
      this.photoRemoved = false;
      this.selectedEventType = '';
      this._previousEventType = '';
      this.editEventTypeName = '';
      this.eventItems = [];
      this.existingItems = [];
      this.errors = {};
      this.loadError = '';
      this.loadingData = false;
      this.activeMainTab = 'evento';
      this.$router.replace({ query: { ...this.$route.query, tab: undefined } });
      this.mobileActiveTab = 'catalog';
      this.inviteData = { location: '', dressCode: '', eventDate: '', eventTime: '', photos: [] };
      this.removedInvitePhotoURLs = [];
      this.mobileEventData = {
        eventId: this.eventId,
        eventType: null,
        eventTypeDisplay: '',
        selectedItems: {},
        eventName: '',
        eventDate: '',
        eventPhotoUrl: null,
        eventPhotoFile: null,
      };
      this.fetchEventTypes();
    },

    // ── Placeholder rotation ─────────────────────────
    _startPlaceholderRotation() {
      this._placeholderTimer = setInterval(() => {
        this._placeholderIndex = (this._placeholderIndex + 1) % this._eventNamePlaceholders.length;
        this.eventNamePlaceholder = this._eventNamePlaceholders[this._placeholderIndex];
      }, 4000);
    },

    // ── Catalog API ──────────────────────────────────
    async fetchEventTypes() {
      this.loadingTypes = true;
      try {
        const cached = getWithExpiry(CACHE_KEY_EVENT_TYPES);
        // Invalida cache antigo que não possui has_rsvp
        if (cached && cached.length > 0 && cached[0].has_rsvp === undefined) {
          localStorage.removeItem(CACHE_KEY_EVENT_TYPES);
        } else if (cached) {
          this.eventTypes = cached;
          return;
        }
        const res = await api.get('/events/type');
        this.eventTypes = res.data;
        setWithExpiry(CACHE_KEY_EVENT_TYPES, res.data, CACHE_TTL_HOURS);
      } catch (e) {
        console.error('Erro ao carregar tipos de evento:', e);
      } finally {
        this.loadingTypes = false;
      }
    },

    async onEventTypeChange(event) {
      const newTypeId = event?.target?.value ?? this.selectedEventType;
      // If there are already selected items, ask for confirmation first
      if (this.selectedItemsList.length > 0) {
        // Revert the select visually to old value while we wait
        this.confirmTypeChangeModal = { visible: true, pendingTypeId: newTypeId };
        // Restore previous value in v-model until user confirms
        this.selectedEventType = this._previousEventType ?? '';
        return;
      }
      this._previousEventType = newTypeId;
      this.selectedEventType = newTypeId;
      await this._applyEventTypeChange(newTypeId);
    },

    async _applyEventTypeChange(typeId) {
      this.errors.eventType = null;
      // Remove custom items from old type before loading new ones
      this.eventItems = this.eventItems.filter(i => !i.isCustom);
      await this.fetchEventItems(typeId);
      const type = this.eventTypes.find(t => t.id === typeId);
      if (type?.placeholders?.length) {
        this.customItemPlaceholder = type.placeholders[0];
      }
    },

    cancelTypeChange() {
      this.confirmTypeChangeModal = { visible: false, pendingTypeId: null };
    },

    async confirmTypeChange() {
      const newTypeId = this.confirmTypeChangeModal.pendingTypeId;
      this.confirmTypeChangeModal = { visible: false, pendingTypeId: null };
      // Clear all current items (selected + custom)
      this.eventItems = [];
      this._previousEventType = newTypeId;
      this.selectedEventType = newTypeId;
      await this._applyEventTypeChange(newTypeId);
    },

    async fetchEventItems(typeId) {
      if (!typeId) {
        this.eventItems = [];
        return;
      }
      this.loadingItems = true;
      try {
        const res = await api.get(`/events/type/${typeId}`);
        this.eventItems = res.data.map(item => ({
          id: item.id,
          name: item.description,
          price: item.price,
          imageUrl: item.photo_url,
          category: item.category,
          isSelected: false,
          selectedAt: null,
          isCustom: false,
          quantity: 1,
        }));
      } catch (e) {
        console.error('Erro ao buscar itens:', e);
        this.eventItems = [];
      } finally {
        this.loadingItems = false;
      }
    },

    // ── Item selection ───────────────────────────────
    toggleItem(itemId) {
      const item = this.eventItems.find(i => i.id === itemId);
      if (item && !item.isLocked) {
        item.isSelected = !item.isSelected;
        item.selectedAt = item.isSelected ? Date.now() : null;
      }
    },

    removeSelectedItem(itemId) {
      const idx = this.eventItems.findIndex(i => i.id === itemId);
      if (idx === -1) return;
      const item = this.eventItems[idx];
      if (item.isLocked) return;
      if (item.isCustom) {
        this.eventItems.splice(idx, 1);
      } else {
        item.isSelected = false;
        item.selectedAt = null;
      }
    },

    // ── Edit modal ───────────────────────────────────
    openEditModal(item) {
      this.editModal = {
        visible: true,
        item,
        editedName: item.name,
        editedPrice: item.price,
        editedImagePreview: item.imageUrl || null,
        errors: {},
      };
    },
    closeEditModal() {
      this.editModal.visible = false;
      this.editModal.item = null;
    },
    triggerEditItemFileInput() {
      this.$refs.editItemFileInput?.click();
    },
    removeEditItemImage() {
      this.editModal.editedImagePreview = null;
    },
    handleEditItemImageUpload(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        toast.error('Use uma imagem JPG ou PNG.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        this.cropperModal = { visible: true, src: e.target.result, forEditItem: true };
      };
      reader.readAsDataURL(file);
      event.target.value = '';
    },
    confirmEditModal() {
      const errs = {};
      if (this.editModal.item.isCustom && !this.editModal.editedName?.trim()) {
        errs.name = 'O nome é obrigatório.';
      }
      const priceError = this.validateItemPriceLimit(this.editModal.editedPrice);
      if (priceError) errs.price = priceError;
      if (Object.keys(errs).length) {
        this.editModal.errors = errs;
        return;
      }
      const item = this.eventItems.find(i => i.id === this.editModal.item.id);
      if (item) {
        if (item.isCustom) item.name = this.editModal.editedName.trim();
        item.price = this.editModal.editedPrice;
        if (this.editModal.editedImagePreview) item.imageUrl = this.editModal.editedImagePreview;
      }
      this.closeEditModal();
      toast.success('Presente atualizado!', { autoClose: 2000, position: 'top-right' });
    },

    // ── Add custom modal ─────────────────────────────
    openAddCustomModal() {
      this.customItemModal = {
        visible: true,
        name: '',
        price: null,
        imagePreview: null,
        imageBlob: null,
        errors: {},
      };
    },
    closeAddCustomModal() {
      this.customItemModal.visible = false;
    },
    addCustomItem() {
      const errs = {};
      if (!this.customItemModal.name.trim()) errs.name = 'O nome é obrigatório.';
      const priceError = this.validateItemPriceLimit(this.customItemModal.price);
      if (priceError) errs.price = priceError;
      if (Object.keys(errs).length) {
        this.customItemModal.errors = errs;
        return;
      }
      const newItem = {
        id: uuidv4(),
        name: this.customItemModal.name.trim(),
        price: this.customItemModal.price,
        imageUrl: this.customItemModal.imagePreview || giftCustomImage,
        category: 'Personalizado',
        isSelected: true,
        selectedAt: Date.now(),
        isCustom: true,
        quantity: 1,
      };
      this.eventItems.unshift(newItem);
      this.closeAddCustomModal();
      toast.success('Presente adicionado!', { autoClose: 2000, position: 'top-right' });
    },

    // ── Custom item image upload ──────────────────────
    triggerCustomItemFileInput() {
      this.$refs.customItemFileInput?.click();
    },
    removeCustomItemImage() {
      this.customItemModal.imagePreview = null;
      this.customItemModal.imageBlob = null;
    },
    handleCustomItemImageUpload(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        toast.error('Use uma imagem JPG ou PNG.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        this.cropperModal = { visible: true, src: e.target.result, forCustomItem: true };
      };
      reader.readAsDataURL(file);
      event.target.value = '';
    },

    // ── Event photo upload ────────────────────────────
    triggerDesktopFileInput() {
      this.$refs.desktopFileInput?.click();
    },
    triggerMobileFileInput() {
      this.$refs.mobileFileInput?.click();
    },
    onMobileScroll(e) {
      this.mobileHeaderCollapsed = e.target.scrollTop > 40;
    },
    removePhoto() {
      this.eventPhoto = null;
      if (this.eventPhotoPreview?.startsWith('blob:')) URL.revokeObjectURL(this.eventPhotoPreview);
      this.eventPhotoPreview = null;
      if (this.isEditMode) this.photoRemoved = true;
    },
    handlePhotoUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const MAX = 1024;
          let w = img.width, h = img.height;
          if (w > h) { if (w > MAX) { h *= MAX / w; w = MAX; } }
          else { if (h > MAX) { w *= MAX / h; h = MAX; } }
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          this.cropperModal = { visible: true, src: canvas.toDataURL('image/jpeg', 0.8), forCustomItem: false };
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
      event.target.value = '';
    },

    // ── Cropper ──────────────────────────────────────
    onCropConfirm({ blob, dataUrl }) {
      if (this.cropperModal.forEditItem) {
        this.editModal.editedImagePreview = dataUrl;
      } else if (this.cropperModal.forCustomItem) {
        this.customItemModal.imagePreview = dataUrl;
        this.customItemModal.imageBlob = blob;
      } else {
        if (this.eventPhotoPreview?.startsWith('blob:')) URL.revokeObjectURL(this.eventPhotoPreview);
        const fileName = `event-photo-${Date.now()}.jpeg`;
        this.eventPhoto = new File([blob], fileName, { type: 'image/jpeg' });
        this.eventPhotoPreview = dataUrl;
      }
      this.cropperModal.visible = false;
      this.cropperModal.src = '';
    },
    cancelCrop() {
      this.cropperModal.visible = false;
      this.cropperModal.src = '';
    },

    // ── Edit mode: load event data ───────────────────
    async fetchEventForEdit() {
      this.loadingData = true;
      this.loadError = '';
      try {
        const { data } = await api.get(`/events/${this.$route.params.id}/edit`);
        this.eventName = data.name ?? '';
        this.eventPhotoPreview = data.photo_url ?? null;
        this.selectedEventType = data.event_type_id;
        this.editEventTypeName = data.event_type_name ?? '';
        this.existingItems = data.items ?? [];

        const existingIds = new Set(this.existingItems.map(i => i.id));

        const catalogItems = (data.catalog_items ?? []).map(ci => ({
          id: ci.id,
          name: ci.name,
          price: ci.price,
          imageUrl: ci.image_url,
          category: ci.category,
          isSelected: existingIds.has(ci.id),
          isLocked: existingIds.has(ci.id),
          selectedAt: existingIds.has(ci.id) ? Date.now() : null,
          isCustom: false,
          quantity: 1,
        }));

        // Items already on event but not in catalog (custom or removed from catalog)
        this.existingItems.forEach(ei => {
          if (!catalogItems.some(ci => ci.id === ei.id)) {
            catalogItems.push({
              id: ei.id,
              name: ei.name,
              price: ei.price,
              imageUrl: ei.image_url || null,
              category: ei.category ?? 'Personalizado',
              isSelected: true,
              isLocked: true,
              selectedAt: Date.now(),
              isCustom: false,
              quantity: ei.quantity ?? 1,
            });
          }
        });

        this.eventItems = catalogItems;

        // Dados do convite digital
        this.removedInvitePhotoURLs = [];
        this.inviteData = {
          location: data.invite_location || '',
          dressCode: data.invite_dress_code || '',
          eventDate: data.invite_date || '',
          eventTime: data.invite_time || '',
          photos: (data.invite_photo_urls || []).map(url => ({ url, dataUrl: url, file: null })),
        };
      } catch (err) {
        if (err.response?.status === 403) this.loadError = 'Este evento não pode ser editado.';
        else if (err.response?.status === 404) this.loadError = 'Evento não encontrado.';
        else this.loadError = 'Erro ao carregar o evento. Tente novamente.';
      } finally {
        this.loadingData = false;
      }
    },

    // ── Edit mode: save changes ──────────────────────
    async handleEditSave() {
      this.errors = {};
      if (!this.eventName.trim() || this.eventName.trim().length < 3) {
        this.errors.eventName = 'O nome do evento deve ter no mínimo 3 caracteres.';
        return;
      }

      this.showLoader = true;
      this.loaderMessage = 'Salvando alterações...';
      try {
        const formData = new FormData();
        formData.append('eventName', this.eventName.trim());

        const newItems = this.selectedItemsList.filter(i => !i.isLocked);
        if (newItems.length > 0) {
          const limitsError = this.validateSelectedItemsLimits(newItems);
          if (limitsError) {
            toast.error(limitsError, { autoClose: 3000, position: 'top-right' });
            return;
          }
          formData.append('newItems', JSON.stringify(
            newItems.map(i => ({
              id: i.id,
              name: i.name,
              price: i.price,
              quantity: i.quantity ?? 1,
              imageUrl: i.imageUrl ?? '',
              category: i.category ?? 'Personalizado',
            }))
          ));
        }

        if (this.eventPhoto) {
          formData.append('eventPhoto', this.eventPhoto);
        }
        if (this.photoRemoved && !this.eventPhoto) {
          formData.append('removePhoto', 'true');
        }

        // Dados do convite digital
        formData.append('invite_location_set', '1');
        formData.append('invite_location', this.inviteData.location || '');
        formData.append('invite_dress_code_set', '1');
        formData.append('invite_dress_code', this.inviteData.dressCode || '');
        formData.append('invite_date_set', '1');
        formData.append('invite_date', this.inviteData.eventDate || '');
        formData.append('invite_time_set', '1');
        formData.append('invite_time', this.inviteData.eventTime || '');

        // URLs existentes a manter e fotos novas
        const keepURLs = [];
        const removedURLs = [];
        const photos = this.inviteData.photos || [];

        // Detectar fotos removidas (URLs que existiam mas não estão mais)
        // inviteData.photos tem {url, dataUrl, file}
        // slots com file=null e url existente → manter
        // slots com file → novo upload
        photos.forEach((slot, idx) => {
          if (slot && slot.file) {
            formData.append(`invite_photo_${idx}`, slot.file);
          } else if (slot && slot.url) {
            keepURLs.push(slot.url);
          }
        });

        formData.append('invite_photo_urls', JSON.stringify(keepURLs));
        if (this.removedInvitePhotoURLs.length > 0) {
          formData.append('invite_removed_photos', JSON.stringify(this.removedInvitePhotoURLs));
        }

        await api.patch(`/events/${this.$route.params.id}`, formData);

        toast.success('Evento atualizado com sucesso! 🎉', { autoClose: 3000, position: 'top-right' });
        setTimeout(() => {
          this.$router.push('/my-account/events');
        }, 1500);
      } catch (err) {
        if (err.response?.status === 403) {
          toast.error('Você não tem permissão para editar este evento.');
        } else {
          toast.error('Erro ao salvar. Tente novamente.');
        }
      } finally {
        this.showLoader = false;
      }
    },

    // ── Invite data update ───────────────────────────
    handleInviteDataUpdate(data) {
      // Rastrear URLs que existiam e foram removidas
      const prevURLs = (this.inviteData.photos || []).map(p => p && p.url).filter(Boolean);
      const newURLs = new Set((data.photos || []).map(p => p && p.url).filter(Boolean));
      prevURLs.forEach(url => {
        if (!newURLs.has(url) && !this.removedInvitePhotoURLs.includes(url)) {
          this.removedInvitePhotoURLs.push(url);
        }
      });
      this.inviteData = { ...data };
    },

    // ── Desktop finalize ─────────────────────────────
    async handleDesktopFinalize() {
      if (this.isEditMode) {
        await this.handleEditSave();
        return;
      }

      this.errors = {};
      let hasError = false;

      if (!this.selectedEventType) {
        this.errors.eventType = 'Selecione uma ocasião.';
        hasError = true;
      }
      if (!this.eventName.trim() || this.eventName.trim().length < 3) {
        this.errors.eventName = 'O nome do evento deve ter no mínimo 3 caracteres.';
        hasError = true;
      }
      if (this.selectedItemsList.length === 0) {
        toast.error('Selecione ao menos um presente para o seu evento.', { autoClose: 3000, position: 'top-right' });
        hasError = true;
      }

      const limitsError = this.validateSelectedItemsLimits();
      if (limitsError) {
        toast.error(limitsError, { autoClose: 3000, position: 'top-right' });
        hasError = true;
      }

      if (hasError) return;

      await this.handleFinalizeEvent({
        eventTypeId: this.selectedEventType,
        eventName: this.eventName.trim(),
        eventPhotoFile: this.eventPhoto,
        selectedItems: this._buildSelectedItemsPayload(),
      });
    },

    _buildSelectedItemsPayload() {
      return this.selectedItemsList.reduce((acc, item) => {
        const clean = {
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          imageUrl: item.imageUrl,
          category: item.category,
        };
        acc[item.id] = clean;
        return acc;
      }, {});
    },

    // ── Shared finalize (API call) ───────────────────
    async handleFinalizeEvent(payload) {
      try {
        this.showLoader = true;
        this.loaderMessage = 'Criando seu evento...';
        const newEventId = this.eventId || crypto.randomUUID();
        const formData = new FormData();
        formData.append('eventId', newEventId);
        formData.append('eventTypeId', payload.eventTypeId);
        formData.append('eventName', payload.eventName);
        if (Object.keys(payload.selectedItems).length > 0) {
          formData.append('selectedItems', JSON.stringify(payload.selectedItems));
        }
        if (payload.eventPhotoFile) {
          formData.append('eventPhoto', payload.eventPhotoFile);
        }

        const response = await api.post('/events/create', formData);
        this.createdEventId = response.data.id || newEventId;
        this.createdEventName = response.data.baby_name;
        this.createdEventShareUrl = `${window.location.origin}/event/${this.createdEventId}`;
        this.createdEventHasRsvp = this.currentEventTypeHasRsvp;
        this.showEventSuccessModal = true;
      } catch (error) {
        const msg = error.response?.data?.message || error.message || 'Erro ao criar evento.';
        toast.error(`Erro: ${msg}`, { autoClose: 5000, position: 'top-right' });
      } finally {
        this.showLoader = false;
      }
    },

    // ── Mobile: step navigation ──────────────────────
    goToStep(stepId) {
      if (stepId === this.currentStep + 1) this.nextStep();
      else if (stepId < this.currentStep) this.currentStep = stepId;
    },
    async nextStep() {
      if (this.currentStep === 1) {
        const ref = this.$refs.step1;
        await new Promise(r => setTimeout(r, 50));
        const result = ref?.validateAndProceed
          ? await ref.validateAndProceed(true)
          : { isValid: true };
        if (result?.isValid !== false && this.currentStep < this.steps.length) {
          this.currentStep++;
        } else if (result?.message) {
          toast.error(result.message, { autoClose: 3000, position: 'top-right' });
        }
      } else if (this.currentStep < this.steps.length) {
        this.currentStep++;
      }
    },
    prevStep() {
      if (this.currentStep > 1) this.currentStep--;
      else this.$router.back();
    },

    // ── Mobile: event steps handlers ─────────────────
    handleEventTypeUpdate(data) {
      this.mobileEventData.eventType = data.eventType;
      const type = this.$refs.step1?.$refs?.step1Ref?.eventTypes?.find(t => t.id === data.eventType);
      this.mobileEventData.eventTypeDisplay = type?.name || (data.eventType ? '' : '');
    },
    handleSelectedItemsUpdate(items) {
      this.mobileEventData.selectedItems = items;
    },
    handleStep2EventDetailsUpdate(data) {
      if (data.eventName !== undefined) this.mobileEventData.eventName = data.eventName;
      if (data.eventPhoto !== undefined) {
        if (this.mobileEventData.eventPhotoUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(this.mobileEventData.eventPhotoUrl);
        }
        if (data.eventPhoto instanceof File) {
          this.mobileEventData.eventPhotoFile = data.eventPhoto;
          this.mobileEventData.eventPhotoUrl = URL.createObjectURL(data.eventPhoto);
        } else {
          this.mobileEventData.eventPhotoFile = null;
          this.mobileEventData.eventPhotoUrl = null;
        }
      }
    },

    // Mobile finalize: called from "Criar meu evento" footer button
    async handleMobileFinalize() {
      const ref = this.$refs.step2;
      const result = ref?.validateAndProceed ? ref.validateAndProceed(true) : { isValid: true };
      if (!result.isValid) return;

      const mobileItems = Object.values(this.mobileEventData.selectedItems || {});
      const limitsError = this.validateSelectedItemsLimits(mobileItems);
      if (limitsError) {
        toast.error(limitsError, { autoClose: 3000, position: 'top-right' });
        return;
      }

      await this.handleFinalizeEvent({
        eventTypeId: this.mobileEventData.eventType,
        eventName: this.mobileEventData.eventName,
        eventPhotoFile: this.mobileEventData.eventPhotoFile,
        selectedItems: this._sanitizeItems(this.mobileEventData.selectedItems),
      });
    },
    _sanitizeItems(items) {
      const src = items && typeof items === 'object' ? items : {};
      const out = {};
      Object.entries(src).forEach(([id, item]) => {
        if (!item || typeof item !== 'object') return;
        const c = { ...item };
        delete c.withdrawal_mode;
        delete c.withdrawalMode;
        out[id] = c;
      });
      return out;
    },
  },
};
</script>

<style scoped>
/* ── Root ──────────────────────────────────────────────── */
.create-event-v2 {
  height: 100%;
  background: #f8f9fa;
}

/* ══════════════════════════════════════════════════════════
   DESKTOP STYLES
══════════════════════════════════════════════════════════ */
.desktop-layout {
  height: 100%;
}

/* Top bar */
.desktop-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.9rem 2rem;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  flex-shrink: 0;
}

.desktop-topbar__back {
  background: none;
  border: 1.5px solid #e0e0e0;
  border-radius: 0.6rem;
  color: #6c757d;
  padding: 0.45rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s;
}
.desktop-topbar__back:hover {
  border-color: #adb5bd;
  color: #32355d;
}

.desktop-topbar__title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #32355d;
  margin: 0;
}

/* Workspace */
.split-workspace {
  display: flex;
  flex: 1;
  overflow: hidden;
  height: 100%;
}

/* ── LEFT PANEL ──────────────────────────────────────── */
.split-left {
  flex: 0 0 33%;
  overflow: hidden;
  border-right: 1px solid #efe8ec;
  background: #fefcfd;
  display: flex;
  flex-direction: column;
}

.split-left__fixed {
  padding: 1.5rem 1.5rem 0.85rem;
  flex-shrink: 0;
  background: #fefcfd;
  border-bottom: 1px solid #f0e8ed;
}

.split-left__scroll {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.5rem 1.5rem;
}

.split-left__add-custom {
  padding-top: 0.65rem;
  margin-top: 0.35rem;
  border-top: 1px solid #f0e8ed;
}

.panel-section-header {
  margin-bottom: 1.25rem;
}
.panel-section-header__title {
  font-size: 1rem;
  font-weight: 700;
  color: #32355d;
  margin: 0 0 0.2rem;
}
.panel-section-header__sub {
  font-size: 0.8rem;
  color: #9e9e9e;
  margin: 0;
}

/* Occasion dropdown */
.occasion-select-wrap {
  position: relative;
}
.occasion-select {
  appearance: none;
  font-size: 0.9rem;
  border-radius: 0.6rem;
  border: 1.5px solid #e0e0e0;
  padding: 0.55rem 2.2rem 0.55rem 0.85rem;
  color: #32355d;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.18s;
  width: 100%;
}
.occasion-select:focus {
  border-color: #ff50a3;
  outline: none;
  box-shadow: 0 0 0 3px rgba(255,80,163,0.12);
}
.occasion-select__icon {
  position: absolute;
  right: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9e9e9e;
  pointer-events: none;
  font-size: 0.75rem;
}

/* Loading / empty */
.catalog-loading,
.catalog-empty {
  padding: 2rem 1rem;
  text-align: center;
  color: #9e9e9e;
  font-size: 0.875rem;
}
.catalog-empty__icon {
  font-size: 2rem;
  margin-bottom: 0.75rem;
  opacity: 0.3;
  display: block;
}

/* Catalog list */
.catalog-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.catalog-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  border-radius: 0.75rem;
  border: 1.5px solid #f0f0f0;
  background: #fafafa;
  transition: border-color 0.18s, background 0.18s;
}
.catalog-row--selected {
  border-color: #ff50a3;
  background: #fff5fa;
}

.catalog-row__img {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 0.5rem;
  flex-shrink: 0;
  background: #f0f0f0;
}

.catalog-row__info {
  flex: 1;
  min-width: 0;
}
.catalog-row__name {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: #32355d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Mobile: allow text wrap */
@media (max-width: 767.98px) {
  .catalog-row__name {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    line-height: 1.3;
  }
}
.catalog-row__price {
  display: block;
  font-size: 0.8rem;
  color: #ff50a3;
  font-weight: 700;
}

.catalog-row__actions {
  display: flex;
  gap: 0.4rem;
  align-items: center;
  flex-shrink: 0;
}

.catalog-row__btn {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  border: none;
  border-radius: 0.5rem;
  padding: 0.35rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s;
}
.catalog-row__btn--edit {
  background: #f3f4f6;
  color: #6c757d;
}
.catalog-row__btn--edit:hover {
  background: #e9ecef;
  color: #32355d;
}
.catalog-row__btn--add {
  background: #ff50a3;
  color: #fff;
}
.catalog-row__btn--add:hover {
  background: #e8417f;
}
.catalog-row__btn--added {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1.5px solid #a5d6a7;
}
.catalog-row__btn--added:hover {
  background: #ffebee;
  color: #c62828;
  border-color: #ef9a9a;
}

/* Add custom button (left panel) — subtle link style */
.btn-add-custom-link {
  background: none;
  border: none;
  padding: 0.25rem 0;
  color: #ff50a3;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  transition: opacity 0.15s;
}
.btn-add-custom-link:hover {
  opacity: 0.75;
}

/* ── RIGHT PANEL ─────────────────────────────────────── */
.split-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f8f9fa;
}

.split-right__fixed {
  flex-shrink: 0;
  padding: 1.25rem 2rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #ececec;
}

.split-right__scroll {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem 2rem 1.5rem;
}

/* Event identity card */
.event-identity-card {
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  background: #fff;
  border-radius: 1rem;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 1px 6px rgba(0,0,0,0.06);
}
.event-identity-card__photo-col {
  flex-shrink: 0;
}
.event-identity-card__name-col {
  flex: 1;
  min-width: 0;
}
.event-identity-card__label {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.4rem;
}
.event-identity-card__input {
  width: 100%;
  border: none;
  border-bottom: 2px solid #e0e0e0;
  border-radius: 0;
  padding: 0.4rem 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #32355d;
  background: transparent;
  transition: border-color 0.18s;
  outline: none;
}
.event-identity-card__input:focus {
  border-color: #ff50a3;
}
.event-identity-card__input.is-invalid {
  border-color: #dc3545;
}
.event-identity-card__hint {
  font-size: 0.75rem;
  color: #bbb;
  margin: 0.35rem 0 0;
}

/* Selected section */
.selected-section {
  background: #fff;
  border-radius: 1rem;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 1px 6px rgba(0,0,0,0.06);
}

.selected-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}
.selected-section__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #32355d;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.selected-section__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  background: #ff50a3;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 99px;
}
.selected-section__total {
  font-size: 0.82rem;
  color: #6c757d;
}
.selected-section__total strong {
  color: #32355d;
}

/* Empty state (right) */
.selected-empty {
  text-align: center;
  padding: 2.5rem 1rem;
}
.selected-empty__icon {
  font-size: 2.5rem;
  color: #e0e0e0;
  margin-bottom: 1rem;
}
.selected-empty__text {
  font-size: 0.9rem;
  font-weight: 600;
  color: #9e9e9e;
  margin-bottom: 0.3rem;
}
.selected-empty__hint {
  font-size: 0.8rem;
  color: #bbb;
  margin: 0;
}

/* Selected list */
.selected-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.selected-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  border-radius: 0.75rem;
  background: #fafafa;
  border: 1.5px solid #f0f0f0;
}
.selected-row__img {
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 0.5rem;
  flex-shrink: 0;
  background: #f0f0f0;
}
.selected-row__info {
  flex: 1;
  min-width: 0;
}
.selected-row__name {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #32355d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.selected-row__price {
  display: block;
  font-size: 0.82rem;
  color: #ff50a3;
  font-weight: 700;
}
.selected-row__badge {
  display: inline-block;
  font-size: 0.68rem;
  font-weight: 600;
  color: #6c757d;
  background: #f0f0f0;
  border-radius: 99px;
  padding: 1px 7px;
  margin-top: 2px;
}
.selected-row__actions {
  display: flex;
  gap: 0.4rem;
  flex-shrink: 0;
}
.selected-row__btn {
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  transition: all 0.18s;
}
.selected-row__btn--edit {
  background: #f3f4f6;
  color: #6c757d;
}
.selected-row__btn--edit:hover { background: #e9ecef; color: #32355d; }
.selected-row__btn--remove {
  background: #f8d7da;
  color: #dc3545;
}
.selected-row__btn--remove:hover { background: #f5c6cb; }

/* Add custom card (right panel) */
.add-custom-card {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.75rem;
  border: 1.5px dashed #d0d0d0;
  border-radius: 0.75rem;
  background: none;
  color: #9e9e9e;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s;
  margin-top: 0.5rem;
}
.add-custom-card:hover {
  border-color: #ff50a3;
  color: #ff50a3;
}
.add-custom-card__icon {
  font-size: 1.1rem;
}

/* Right panel footer */
.split-right__footer {
  padding: 1.25rem 2rem;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  box-shadow: 0 -2px 10px rgba(0,0,0,0.04);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.btn-add-custom-footer {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: 1.5px solid #ff50a3;
  color: #ff50a3;
  border-radius: 0.65rem;
  padding: 0.6rem 1.2rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s;
  white-space: nowrap;
}
.btn-add-custom-footer:hover {
  background: #fff0f7;
}

.btn-create-event {
  background: #ff50a3;
  color: #fff;
  border: none;
  border-radius: 0.75rem;
  padding: 0.7rem 2.5rem;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.18s, transform 0.15s, box-shadow 0.18s;
}
.btn-create-event:hover:not(:disabled) {
  background: #e8417f;
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(255,80,163,0.35);
}
.btn-create-event:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modals shared */
.modal-buttons-row {
  display: flex;
  gap: 0.75rem;
}
.btn-modal-confirm {
  background: #ff50a3;
  color: #fff;
  border: none;
  font-weight: 600;
}
.btn-modal-confirm:hover {
  background: #e8417f;
  color: #fff;
}

/* Transition: item list animation */
.item-list-enter-active {
  transition: all 0.25s ease;
}
.item-list-enter-from {
  opacity: 0;
  transform: translateX(-12px);
}
.item-list-leave-active {
  transition: all 0.2s ease;
}
.item-list-leave-to {
  opacity: 0;
  transform: translateX(12px);
}

/* ══════════════════════════════════════════════════════════
   MOBILE STYLES — cart layout
══════════════════════════════════════════════════════════ */
@media (max-width: 767.98px) {

.mobile-layout {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  height: 100%;
  background: #f8f9fa;
  position: relative;
  overflow: hidden;
}

/* ── Collapsible header ────────────────────────────────── */
.mob-header {
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  transition: max-height 0.3s ease, padding 0.3s ease, opacity 0.25s ease;
  max-height: 160px;
  overflow: hidden;
  flex-shrink: 0;
}
.mob-header--collapsed {
  max-height: 0;
  opacity: 0;
  pointer-events: none;
}
.mob-header__inner {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.85rem 1rem;
}
.mob-header__photo { flex-shrink: 0; }
.mob-header__name-wrap { flex: 1; min-width: 0; }
.mob-header__label {
  display: block;
  font-size: 0.7rem;
  font-weight: 700;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.3rem;
}
.mob-header__input {
  width: 100%;
  border: none;
  border-bottom: 2px solid #e0e0e0;
  border-radius: 0;
  padding: 0.3rem 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #32355d;
  background: transparent;
  outline: none;
  transition: border-color 0.18s;
}
.mob-header__input:focus { border-color: #ff50a3; }
.mob-header__input.is-invalid { border-color: #dc3545; }

/* ── Desktop main tabs (edit mode) ─────────────────────── */
.desktop-main-tabs {
  display: flex;
  align-items: flex-end;
  border-bottom: 2px solid #e9ecef;
  background: #fff;
  flex-shrink: 0;
  padding: 0.75rem 1.5rem 0;
  gap: 0.25rem;
}
.desktop-main-tab {
  padding: 0.6rem 1.25rem;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  margin-bottom: -2px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6c757d;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  transition: color 0.15s, background 0.15s, border-color 0.15s;
  outline: none;
  box-shadow: none;
}
.desktop-main-tab:hover {
  color: #ff50a3;
  background: #fff0f7;
  border-color: #ffc8e3;
}
.desktop-main-tab--active {
  color: #ff50a3;
  background: #fff;
  border-color: #e9ecef;
  border-bottom: 2px solid #fff;
}
.desktop-main-tab__badge {
  font-size: 0.6rem;
  background: #ff50a3;
  color: #fff;
  border-radius: 99px;
  padding: 1px 6px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.desktop-invite-panel {
  flex: 1;
  overflow-y: auto;
  background: #f4f6f8;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1.5rem;
}
.desktop-invite-panel__card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07);
  padding: 2rem;
  width: 100%;
  max-width: 750px;
}

/* ── Tabs ──────────────────────────────────────────────── */
.mob-tabs {
  display: flex;
  background: #fff;
  border-bottom: 2px solid #f0f0f0;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 10;
}
.mob-tab {
  flex: 1;
  padding: 0.75rem 0.5rem;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #9e9e9e;
  cursor: pointer;
  transition: all 0.18s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
}
.mob-tab--active { color: #ff50a3; border-bottom-color: #ff50a3; }
.mob-tab__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: #ff50a3;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  border-radius: 99px;
}
.mob-tab__badge--new { background: #ff50a3; }
.mob-convite-tab { padding: 1rem; }

/* ── Bottom Navigation Bar ──────────────────────────────── */
.mob-bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 62px;
  background: #fff;
  border-top: 1px solid #ececec;
  display: none;
  align-items: stretch;
  z-index: 61;
  box-shadow: 0 -2px 12px rgba(0,0,0,0.07);
}
.mob-bnav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  background: none;
  border: none;
  cursor: pointer;
  color: #adb5bd;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  position: relative;
  transition: color 0.15s;
  padding: 0;
}
.mob-bnav-item i { font-size: 1.15rem; line-height: 1; }
.mob-bnav-item--active { color: #ff50a3; }
.mob-bottom-nav--visible { display: flex !important; }
.mob-bnav-badge {
  position: absolute;
  top: 6px;
  right: calc(50% - 18px);
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  background: #ff50a3;
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  border-radius: 99px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

/* ── Mobile convite save bar ───────────────────────────── */
.mob-convite-save-bar {
  position: fixed;
  bottom: 62px;
  left: 0;
  right: 0;
  z-index: 60;
  padding: 0.75rem 1rem;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  box-shadow: 0 -3px 12px rgba(0,0,0,0.08);
}

/* ── Desktop convite save bar ──────────────────────────── */
.convite-save-bar {
  position: sticky;
  bottom: 0;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  box-shadow: 0 -2px 10px rgba(0,0,0,0.06);
  padding: 0.85rem 1.5rem;
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
  z-index: 10;
}
.convite-save-bar__inner {
  width: 100%;
  max-width: 700px;
  display: flex;
  justify-content: flex-end;
}

/* ── Scrollable content ────────────────────────────────── */
.mob-content {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  min-height: 0;
  padding-bottom: 140px;
}

/* ── Catalog tab ───────────────────────────────────────── */
.mob-occasion-wrap {
  padding: 1rem 1rem 0.75rem;
  background: #fff;
  border-bottom: 1px solid #f5f5f5;
}
.mob-occasion-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 700;
  color: #32355d;
  margin-bottom: 0.5rem;
}
.mob-catalog-list {
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* ── Items tab ─────────────────────────────────────────── */
.mob-items-tab { padding: 1rem; }

/* ── FAB ───────────────────────────────────────────────── */
.mob-fab {
  position: fixed;
  bottom: 132px;
  right: 1.25rem;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #fff;
  color: #ff50a3;
  border: 2px solid #ff50a3;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(255,80,163,0.25), 0 1px 4px rgba(0,0,0,0.08);
  cursor: pointer;
  z-index: 50;
  transition: transform 0.18s, box-shadow 0.18s;
}
.mob-fab:hover { transform: scale(1.08); }

/* ── Sticky cart footer ────────────────────────────────── */
.mob-cart-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #fff;
  border-top: 1px solid #f0f0f0;
  box-shadow: 0 -3px 12px rgba(0,0,0,0.08);
}
.mob-cart-bar__summary {
  display: flex;
  flex-direction: column;
  cursor: pointer;
  min-width: 0;
}
.mob-cart-bar__count { font-size: 0.78rem; font-weight: 600; color: #32355d; }
.mob-cart-bar__total { font-size: 0.85rem; font-weight: 800; color: #ff50a3; }
.mob-cart-bar__btn {
  background: #ff50a3;
  color: #fff;
  border: none;
  border-radius: 0.75rem;
  padding: 0.65rem 1.25rem;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.18s, transform 0.15s;
}
.mob-cart-bar__btn:hover:not(:disabled) { background: #e8417f; transform: translateY(-1px); }
.mob-cart-bar__btn:disabled { opacity: 0.5; cursor: not-allowed; }

} /* end @media mobile */

/* ──────────────────────────────────────────────
   EDIT MODE: loading / error states
────────────────────────────────────────────── */
.edit-loading,
.edit-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  text-align: center;
  padding: 2rem;
}

/* ──────────────────────────────────────────────
   LOCKED TYPE CHIP (edit mode — type cannot change)
────────────────────────────────────────────── */
.locked-type-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #f3f4f6;
  border: 1.5px solid #e8e8e8;
  border-radius: 2rem;
  padding: 0.45rem 0.875rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #32355d;
}
.locked-type-chip__icon { color: #22c55e; font-size: 0.9rem; }
.locked-type-chip__lock { color: #9ca3af; font-size: 0.78rem; margin-left: auto; }

/* ──────────────────────────────────────────────
   CATALOG ROW: locked badge (already on event)
────────────────────────────────────────────── */
.catalog-row--locked { opacity: 0.8; }
.catalog-row__locked-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f3f4f6;
  color: #9ca3af;
  font-size: 0.8rem;
}

/* ──────────────────────────────────────────────
   SELECTED ROW: locked icon (locked items)
────────────────────────────────────────────── */
.selected-row__locked {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f3f4f6;
  color: #9ca3af;
  font-size: 0.8rem;
  flex-shrink: 0;
}
</style>
